import { useTandemTradingBlocked } from '../../../../features/tandem/TandemSessionBoundary';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Col, InputNumber, Row, Select } from 'antd';
import { RetweetOutlined } from '@ant-design/icons';

import { exchangeMoney, selectors, utils, Wallet } from 'state';
import { formatters } from 'services';
import { useLocation } from 'react-router-dom';

const formatter = (prefix: string) => (val: string | number | undefined) => {
  return `${prefix} ${formatters.commasNoDigits(val)}`;
};
const parser = (val: string | undefined) => {
  return `${val
    ?.slice(2)
    .replace(/[A-Za-z,/.`;'=+_{}|:"<>?)(*&^%$#@!~]*/g, '')}`;
};

const CurrencySelect: React.FC<{
  is_this_to_wallet: boolean;
  value: string;
  handleSelect: (wallet: Wallet) => void;
}> = ({ is_this_to_wallet, value, handleSelect }) => {
  const wallets = useSelector(selectors.wallets);
  const currencies = useSelector(selectors.currencies);
  const instruments = useSelector(selectors.financialInstrumentsList);
  const symbols = useSelector(selectors.playableCurrencies(instruments));

  return (
    <Select
      value={value}
      onChange={val => {
        const newWallet = wallets.find(
          item => currencies[item.currency].symbol === val,
        );
        if (!newWallet) return;
        handleSelect(newWallet);
      }}
      style={{ width: 100 }}
    >
      {/* ===== LIMIT TO_WALLET TO USD ==== */}
      {/*{symbols.map(symbol => {*/}
      {/*  if (is_this_to_wallet) {*/}
      {/*    if (symbol === 'USD') {*/}
      {/*      return (*/}
      {/*        <Select.Option key={symbol} value={symbol}>*/}
      {/*          {symbol}*/}
      {/*        </Select.Option>*/}
      {/*      );*/}
      {/*    }*/}
      {/*  } else {*/}
      {/*    return (*/}
      {/*      <Select.Option key={symbol} value={symbol}>*/}
      {/*        {symbol}*/}
      {/*      </Select.Option>*/}
      {/*    );*/}
      {/*  }*/}
      {/*})}*/}

      {/* ===== ALLOW ALL WALLETS ==== */}
      {symbols.map(symbol => {
        return (
          <Select.Option key={symbol} value={symbol}>
            {symbol}
          </Select.Option>
        );
      })}
    </Select>
  );
};

type Props = {
  onComplete: () => void;
  tradingBlocked: boolean;
};

const CashExchangeForm: React.FC<Props> = ({ onComplete, tradingBlocked }) => {
  const tandemBlocked = useTandemTradingBlocked();
  const dispatch = useDispatch();
  const assetList = useSelector(selectors.underlyingAssetsList);
  const currencyList = useSelector(selectors.currenciesList);
  const wallets = useSelector(selectors.wallets);
  const currencies = useSelector(selectors.currencies);
  const { session } = useSelector(selectors.player);
  const reportingCurrency = utils.useReportingCurrency(session);
  const reportingWallet = useSelector(
    selectors.walletByCurrency(reportingCurrency),
  ) as Wallet;

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const do_bypass = params.get('bypass_please') === '34';

  const [fromWallet, setFromWallet] = useState(reportingWallet);
  const [toWallet, setToWallet] = useState(
    wallets.find(wallet => wallet.currency !== reportingCurrency.id) ||
      wallets[0],
  );

  const [fromAmount, setFromAmount] = useState(0);

  useEffect(() => {
    // Get newer wallets
    const newFromWallet = wallets.find(w => w.id === fromWallet?.id);
    const newToWallet = wallets.find(w => w.id === toWallet?.id);

    // If new wallets exist, set them
    if (newFromWallet !== undefined) {
      setFromWallet(newFromWallet);
    } else {
      setFromWallet(wallets[1]);
    }

    if (newToWallet !== undefined) {
      setToWallet(newToWallet);
    } else {
      setToWallet(
        wallets.find(wallet => wallet.currency !== reportingCurrency.id) ||
          wallets[0],
      );
    }
  }, [wallets]);

  const exchangeRate = utils.useExchangeRate(
    fromWallet.currency,
    toWallet.currency,
  );

  const fromSymbol = currencies[fromWallet.currency]?.symbol ?? '';
  const toSymbol = currencies[toWallet.currency]?.symbol ?? '';
  const sameSymbol = fromSymbol === toSymbol;
  const notReporting = ![fromSymbol, toSymbol].includes(
    reportingCurrency.symbol,
  );
  const currency_pair = utils.getCurrencyPair(
    fromWallet.currency,
    toWallet.currency,
    assetList,
  );
  const disabled = sameSymbol || notReporting || currency_pair === undefined;
  //This makes sure inputs are reset to 0 in case of faulty currency pair choice (ex: USD-USD or non USD transactions)
  // useEffect(() => {
  //   if (disabled) setFromAmount(0);
  // }, [exchangeRate]);

  const handleToAmountChange = (toAmount: number | undefined) => {
    // When you input the toAmount top field, this handler fills the fromAmount bottom field.
    if (typeof toAmount !== 'number') {
      return;
    }
    const convertedFromAmount = utils.cashExchange(
      fromWallet.currency,
      toWallet.currency,
      0,
      toAmount,
      assetList,
      currencyList,
    );
    setFromAmount(convertedFromAmount);
  };

  const getToAmount = () => {
    // When you input the fromAmount bottom field, this handler fills the toAmount top field.
    return utils.cashExchange(
      fromWallet.currency,
      toWallet.currency,
      fromAmount,
      0,
      assetList,
      currencyList,
    );
  };

  const handleSwapCurrencies = () => {
    setFromWallet(toWallet);
    setToWallet(fromWallet);

    const newToAmount = getToAmount();
    setFromAmount(newToAmount);
  };

  const handleAllBalance = () => {
    setFromAmount(fromWallet.amount);
  };

  const handleExchange = () => {
    dispatch(
      exchangeMoney({
        from_wallet: fromWallet.id,
        to_wallet: toWallet.id,
        amount: fromAmount,
      }),
    );
    setFromAmount(0);
    onComplete();
  };

  let do_disable =
    fromAmount <= 0 || fromAmount > fromWallet.amount || disabled;
  if (do_bypass) {
    do_disable = false;
  }
  return (
    <div className="markets-v3-form markets-v3-exchange-form">
      <div className="markets-v3-form__body">
        <div className="markets-v3-form__intro">
          Move cash between playable wallets. One side of the conversion must be
          the reporting currency.
        </div>
        <div className="markets-v3-exchange-leg is-receive">
          <span>Receive</span>
          <Row justify="space-between" gutter={12}>
            <Col>
              <CurrencySelect
                is_this_to_wallet={true}
                value={currencies[toWallet.currency].symbol}
                handleSelect={setToWallet}
              />
            </Col>
            <Col flex="auto">
              <InputNumber
                aria-label="Amount received"
                disabled={disabled}
                onChange={handleToAmountChange}
                value={getToAmount()}
                min={0}
                formatter={formatter('+')}
                parser={parser}
              />
            </Col>
          </Row>
        </div>
        <div className="markets-v3-exchange-rate">
          <span>Exchange rate</span>
          <strong>
            {!disabled
              ? formatters.priceFormat(
                  exchangeRate,
                  currency_pair!.ticker,
                  '',
                  currency_pair!.resourcetype,
                )
              : 'Not available'}
          </strong>
        </div>
        <div className="markets-v3-exchange-leg is-pay">
          <span>Pay</span>
          <Row justify="space-between" gutter={12}>
            <Col>
              <CurrencySelect
                is_this_to_wallet={false}
                value={currencies[fromWallet.currency].symbol}
                handleSelect={setFromWallet}
              />
              <small>
                Balance: {formatters.commasNoDigits(fromWallet.amount)}
              </small>
            </Col>
            <Col flex="auto">
              <InputNumber
                aria-label="Amount paid"
                disabled={disabled}
                onChange={val => {
                  if (val === undefined) return;
                  setFromAmount(Number(val));
                }}
                value={fromAmount}
                min={0}
                formatter={formatter('-')}
                parser={parser}
              />
            </Col>
          </Row>
        </div>
      </div>
      <div className="markets-v3-form__footer markets-v3-exchange-actions">
        <Row>
          <Button aria-label="Swap currencies" onClick={handleSwapCurrencies}>
            <RetweetOutlined />
          </Button>
          <Button disabled={disabled} onClick={handleAllBalance}>
            All balance
          </Button>
          <Button
            type="primary"
            disabled={tandemBlocked || tradingBlocked || do_disable}
            onClick={handleExchange}
            style={{ marginLeft: 'auto' }}
          >
            Exchange
          </Button>
        </Row>
      </div>
    </div>
  );
};

export default CashExchangeForm;
